import React, { useState, useRef, useEffect } from 'react';
import { FiSearch, FiChevronDown, FiX } from 'react-icons/fi';
import Icon from '../Icon';

type Option = {
    id: string;
    label: string;
    value: string;
    details?: {
        email?: string;
        mobile?: string;
    };
};

type SearchableSelectProps = {
    options: Option[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    label?: string;
    disabled?: boolean;
    className?: string;
};

export default function SearchableSelect({
    options,
    value,
    onChange,
    placeholder = 'Search and select...',
    label,
    disabled = false,
    className = '',
}: SearchableSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [displayValue, setDisplayValue] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const selectedOption = options.find(opt => opt.value === value);

    useEffect(() => {
        if (selectedOption) {
            setDisplayValue(`${selectedOption.label}${selectedOption.details?.email ? ` (${selectedOption.details.email})` : ''}`);
        } else {
            setDisplayValue('');
        }
    }, [selectedOption]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    const filteredOptions = options.filter(opt => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            opt.label.toLowerCase().includes(search) ||
            opt.details?.email?.toLowerCase().includes(search) ||
            opt.details?.mobile?.includes(search)
        );
    });

    const handleSelect = (optionValue: string) => {
        onChange(optionValue);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange('');
        setDisplayValue('');
        setSearchTerm('');
    };

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            {label && (
                <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                    {label}
                </label>
            )}
            <div className="relative">
                <button
                    type="button"
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    disabled={disabled}
                    className={`w-full rounded-xl border border-gray-300 bg-white py-3 pl-10 pr-10 text-left text-sm outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-gray-400'
                        }`}
                >
                    <Icon
                        icon={FiSearch}
                        className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                    />
                    <span className={`block truncate ${!displayValue ? 'text-gray-400' : ''}`}>
                        {displayValue || placeholder}
                    </span>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                        {value && !disabled && (
                            <button
                                type="button"
                                onClick={handleClear}
                                className="rounded p-0.5 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                <Icon icon={FiX} className="h-4 w-4 text-gray-400" />
                            </button>
                        )}
                        <Icon
                            icon={FiChevronDown}
                            className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        />
                    </div>
                </button>

                {isOpen && (
                    <div className="absolute z-50 mt-1 w-full rounded-xl border border-gray-300 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
                        <div className="border-b border-gray-200 p-2 dark:border-gray-700">
                            <div className="relative">
                                <Icon
                                    icon={FiSearch}
                                    className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                                />
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search by name, email, or mobile..."
                                    className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        </div>
                        <div className="max-h-60 overflow-y-auto">
                            {filteredOptions.length === 0 ? (
                                <div className="px-4 py-3 text-center text-sm text-gray-500 dark:text-gray-400">
                                    No managers found
                                </div>
                            ) : (
                                filteredOptions.map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => handleSelect(option.value)}
                                        className={`w-full px-4 py-3 text-left text-sm transition hover:bg-blue-50 dark:hover:bg-gray-700 ${value === option.value ? 'bg-blue-50 dark:bg-gray-700' : ''
                                            }`}
                                    >
                                        <div className="font-medium text-gray-900 dark:text-gray-100">
                                            {option.label}
                                        </div>
                                        {option.details?.email && (
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                {option.details.email}
                                            </div>
                                        )}
                                        {option.details?.mobile && (
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                {option.details.mobile}
                                            </div>
                                        )}
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>
            {selectedOption && selectedOption.details && (
                <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs dark:border-blue-800 dark:bg-blue-900/20">
                    <div className="font-semibold text-blue-900 dark:text-blue-100">
                        {selectedOption.label}
                    </div>
                    {selectedOption.details.email && (
                        <div className="text-blue-700 dark:text-blue-300">
                            {selectedOption.details.email}
                        </div>
                    )}
                    {selectedOption.details.mobile && (
                        <div className="text-blue-700 dark:text-blue-300">
                            {selectedOption.details.mobile}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
